import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import Layout from "./components/layout/Layout";

// Pages
import Login from "./pages/Login";

// Super Admin
import SuperDashboard from "./pages/superadmin/Dashboard";
import SuperCompanies from "./pages/superadmin/Companies";
import SuperClients from "./pages/superadmin/Clients";
import SuperClientUsers from "./pages/superadmin/ClientUsers"; // ajay:code 11-08-26

// Company Admin
import CompanyDashboard from "./pages/company/Dashboard";
import CompanyMasters from "./pages/company/Masters";
// import CompanyUserForms   from './pages/company/UserForms';
// import CompanyUserFormBuilder from './pages/company/UserFormBuilder';
import WeightHistory from "./pages/company/WeightHistory";
import ClientUsers from "./pages/company/ClientUsers";
import CompanyUsers from "./pages/company/Users";
// import FillForms from './pages/company/FillForms';
import CompanyRoles from "./pages/company/Roles";
// import CompanyScanDashboard from './pages/company/ScanDashboard';
import CompanySettings from "./pages/company/CompanySettings"; // ajay:code 22-08-26

//ajay:code 24-07-26
import Templates from "./pages/company/Templates";
import TemplateView from "./pages/company/TemplateView";

// ajay:code 30-07-26
import PdirMaster from "./pages/company/PdirMaster";
import PdirTemplate from "./pages/company/PdirTemplate";
import PdirForm from "./pages/company/PdirForm";
import ClientPdirTemplate from "./pages/client/PdirTemplate";

// FPIR ajay:code 03-08-26
import FpirMaster from "./pages/company/FpirMaster";
import FpirTemplate from "./pages/company/FpirTemplate";
import FpirForm from "./pages/company/FpirForm";
import ClientFpirTemplate from "./pages/client/FpirTemplate";

// Pfmea ajay:code 19-08-26
import PfmeaTemplate from "./pages/company/PfmeaTemplate";
import PfmeaForm from "./pages/company/PfmeaForm";
import ClientPfmeaTemplate from "./pages/client/PfmeaTemplate";

// controlPlan ajay:code 27-08-26
import ControlPlanTemplate from "./pages/company/ControlPlanTemplate";
import ControlPlanForm from "./pages/company/ControlPlanForm";
import ClientControlPlanTemplate from "./pages/client/ControlPlanTemplate";

// Client
import ClientDashboard from "./pages/client/Dashboard";
import ClientHistory from "./pages/client/WeightHistory";
// import ClientForms      from './pages/client/Forms';
import PagePasswordGate from "./components/ui/PagePasswordGate";
import PasswordGate from "./components/PasswordGate"; // ajay:code 11-08-26

// ── RootRedirect: inside AuthProvider so useAuth works ──
function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.user_type === "superadmin")
    return <Navigate to="/superadmin/dashboard" replace />;
  if (user.user_type === "company" || user.user_type === "company_user")
    return <Navigate to="/company/dashboard" replace />;
  if (user.user_type === "client" || user.user_type === "client_user")
    return <Navigate to="/client/dashboard" replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<RootRedirect />} />

            {/* ── Super Admin ── */}
            <Route
              path="/superadmin"
              element={
                <ProtectedRoute roles={["superadmin"]}>
                  <Layout role="superadmin" />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<SuperDashboard />} />
              <Route path="companies" element={<SuperCompanies />} />
              <Route path="clients" element={<SuperClients />} />
              <Route path="client-users" element={<SuperClientUsers />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>

            {/* ── Company Admin ── */}
            <Route
              path="/company"
              element={
                <ProtectedRoute roles={["company", "company_user"]}>
                  <Layout role="company" />
                </ProtectedRoute>
              }
            >
              <Route
                path="dashboard"
                element={
                  <PagePasswordGate
                    pageKey="view_dashboard"
                    pageLabel="Dashboard"
                  >
                    <CompanyDashboard />
                  </PagePasswordGate>
                }
              />
              {/* company logo ajay:code 22-08-26 */}
              <Route path="settings" element={<CompanySettings />} />

              <Route path="masters" element={<CompanyMasters />} />
              {/* ajay:code 24-07-26 */}
              <Route path="templates" element={<Templates />} />
              <Route path="templates/:templateId" element={<TemplateView />} />

              {/* PDIR ajay:code 30-07-26 */}
              <Route path="pdir-master" element={<PdirMaster />} />
              <Route path="pdir" element={<PdirTemplate />} />
              <Route path="pdir/new" element={<PdirForm />} />
              <Route path="pdir/:id" element={<PdirForm />} />
              <Route path="pdir/:id/edit" element={<PdirForm />} />

              {/* FPIR ajay:code 03-08-26 */}
              <Route path="fpir-master" element={<FpirMaster />} />
              <Route path="fpir" element={<FpirTemplate />} />
              <Route path="fpir/new" element={<FpirForm />} />
              <Route path="fpir/:id" element={<FpirForm />} />
              <Route path="fpir/:id/edit" element={<FpirForm />} />

              {/* PFMEA ajay:code 19-08-26 */}
              <Route path="pfmea" element={<PfmeaTemplate />} />
              <Route path="pfmea/new" element={<PfmeaForm />} />
              {/* <Route path="pfmea/:id" element={<PfmeaForm />} /> */}
              <Route path="pfmea/:id" element={<PfmeaForm sheetView />} />
              <Route path="pfmea/:id/edit" element={<PfmeaForm />} />

              {/* controlPlan ajay:code 27-08-26 */}
              <Route path="control-plan" element={<ControlPlanTemplate />} />
              <Route path="control-plan/new" element={<ControlPlanForm />} />
              {/* <Route path="control-plan/:id" element={<ControlPlanForm />} /> */}
              <Route
                path="control-plan/:id"
                element={<ControlPlanForm sheetView />}
              />
              <Route
                path="control-plan/:id/edit"
                element={<ControlPlanForm />}
              />

              <Route
                path="weight-history"
                element={
                  <PagePasswordGate
                    pageKey="view_weight_history"
                    pageLabel="Weight History"
                  >
                    <WeightHistory />
                  </PagePasswordGate>
                }
              />
              <Route path="client-users" element={<ClientUsers />} />
              <Route
                path="users"
                element={
                  <PagePasswordGate
                    pageKey="manage_users"
                    pageLabel="Manage Users"
                  >
                    <CompanyUsers />
                  </PagePasswordGate>
                }
              />
              <Route path="roles" element={<CompanyRoles />} />
              {/* <Route path="user-fields"          element={<CompanyUserForms />} /> */}
              {/* <Route path="user-fields/new"      element={<CompanyUserFormBuilder />} /> */}
              {/* <Route path="user-fields/:id/edit" element={<CompanyUserFormBuilder />} /> */}
              {/* <Route path="fill-forms" element={<FillForms />} /> */}
              <Route index element={<Navigate to="dashboard" replace />} />
              {/* <Route path="scan-dashboard" element={
              <PagePasswordGate pageKey="view_scan_dashboard" pageLabel="Scan Dashboard">
                <CompanyScanDashboard />
              </PagePasswordGate>
            } /> */}
            </Route>

            {/* ── Client ── */}
            <Route
              path="/client"
              element={
                <ProtectedRoute roles={["client", "client_user"]}>
                  <Layout role="client" />
                </ProtectedRoute>
              }
            >
              {/* <Route
              path="dashboard"
              element={
                <PagePasswordGate
                  pageKey="view_dashboard"
                  pageLabel="Dashboard"
                >
                  <ClientDashboard />
                </PagePasswordGate>
              }
            />
            <Route
              path="history"
              element={
                <PagePasswordGate
                  pageKey="view_weight_history"
                  pageLabel="Weight History"
                >
                  <ClientHistory />
                </PagePasswordGate>
              }
            /> */}

              {/* <Route index element={<Navigate to="dashboard" replace />} /> */}
              {/*PDIR ajay:code 30-07-26 */}
              {/* <Route path="pdir" element={<ClientPdirTemplate />} />
            <Route
              path="pdir/:id"
              element={<PdirForm basePath="/client/pdir" />}
            /> */}
              {/* FPIR ajay:code 03-08-26 */}
              {/* <Route path="fpir" element={<ClientFpirTemplate />} />
            <Route
              path="fpir/:id"
              element={<FpirForm basePath="/client/fpir" />}
            /> */}

              {/* ajay:code 11-08-26 */}
              <Route
                path="dashboard"
                element={
                  <PasswordGate key="dashboard" pageKey="dashboard">
                    <ClientDashboard />
                  </PasswordGate>
                }
              />
              <Route
                path="history"
                element={
                  <PasswordGate key="weight_history" pageKey="weight_history">
                    <ClientHistory />
                  </PasswordGate>
                }
              />
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route
                path="pdir"
                element={
                  <PasswordGate key="pdir" pageKey="pdir">
                    <ClientPdirTemplate />
                  </PasswordGate>
                }
              />
              <Route
                path="pdir/:id"
                element={
                  <PasswordGate key="pdir" pageKey="pdir">
                    <PdirForm basePath="/client/pdir" />
                  </PasswordGate>
                }
              />
              <Route
                path="fpir"
                element={
                  <PasswordGate key="fpir" pageKey="fpir">
                    <ClientFpirTemplate />
                  </PasswordGate>
                }
              />
              <Route
                path="fpir/:id"
                element={
                  <PasswordGate key="fpir" pageKey="fpir">
                    <FpirForm basePath="/client/fpir" />
                  </PasswordGate>
                }
              />
              {/* PFMEA ajay:code 19-08-26 */}
              <Route
                path="pfmea"
                element={
                  <PasswordGate key="pfmea" pageKey="pfmea">
                    <ClientPfmeaTemplate />
                  </PasswordGate>
                }
              />
              <Route
                path="pfmea/:id"
                element={
                  <PasswordGate key="pfmea" pageKey="pfmea">
                    <PfmeaForm basePath="/client/pfmea" sheetView />
                  </PasswordGate>
                }
              />
              {/* controlPlan ajay:code 27-08-26 */}
              <Route
                path="control-plan"
                element={
                  <PasswordGate key="control-plan" pageKey="control-plan">
                    <ClientControlPlanTemplate />
                  </PasswordGate>
                }
              />
              <Route
                path="control-plan/:id"
                element={
                  <PasswordGate key="control-plan" pageKey="control-plan">
                    <ControlPlanForm
                      basePath="/client/control-plan"
                      sheetView
                    />
                  </PasswordGate>
                }
              />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
